<template>
  <b-card>
    <h3>Trip Rating</h3>
    <base-overlay :show="loading">
      <template v-if="!loading && tripRating">
        <b-row>
          <!-- Driver Rating Section -->
          <b-col md="6">
            <b-card class="mb-3">
              <b-card-header>
                <h5 class="mb-0">
                  Driver Rating (Driver's rating of the rider)
                </h5>
              </b-card-header>
              <b-card-body>
                <template v-if="tripRating.driverRating">
                  <div class="d-flex align-items-center mb-2">
                    <div class="rating-stars mr-2">
                      <feather-icon
                        v-for="star in 5"
                        :key="`driver-star-${star}`"
                        :icon="star <= tripRating.driverRating.rating ? 'StarIcon' : 'Star'"
                        :class="star <= tripRating.driverRating.rating ? 'text-warning' : 'text-muted'"
                        size="20"
                      />
                    </div>
                    <span class="h5 mb-0">{{ tripRating.driverRating.rating }}/5</span>
                  </div>

                  <div
                    v-if="tripRating.driverRating.comment"
                    class="mb-2"
                  >
                    <strong>Comment:</strong>
                    <p class="mt-1 text-muted">
                      {{ tripRating.driverRating.comment }}
                    </p>
                  </div>

                  <div class="text-muted">
                    <small>
                      <feather-icon
                        icon="ClockIcon"
                        size="14"
                        class="mr-1"
                      />
                      Submitted: {{ formatDateTime(tripRating.driverRating.createdAt) }}
                    </small>
                  </div>
                </template>
                <template v-else>
                  <div class="text-center text-muted py-3">
                    <feather-icon
                      icon="StarIcon"
                      size="48"
                      class="text-light mb-2"
                    />
                    <p>
                      No driver rating available
                    </p>
                  </div>
                </template>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Rider Rating Section -->
          <b-col md="6">
            <b-card class="mb-3">
              <b-card-header>
                <h5 class="mb-0">
                  Rider Rating (Rider's rating of the driver)
                </h5>
              </b-card-header>
              <b-card-body>
                <template v-if="tripRating.riderRating">
                  <div class="d-flex align-items-center mb-2">
                    <div class="rating-stars mr-2">
                      <feather-icon
                        v-for="star in 5"
                        :key="`rider-star-${star}`"
                        :icon="star <= tripRating.riderRating.rating ? 'StarIcon' : 'Star'"
                        :class="star <= tripRating.riderRating.rating ? 'text-warning' : 'text-muted'"
                        size="20"
                      />
                    </div>
                    <span class="h5 mb-0">{{ tripRating.riderRating.rating }}/5</span>
                  </div>

                  <div
                    v-if="tripRating.riderRating.comment"
                    class="mb-2"
                  >
                    <strong>Comment:</strong>
                    <p class="mt-1 text-muted">
                      {{ tripRating.riderRating.comment }}
                    </p>
                  </div>

                  <div class="text-muted">
                    <small>
                      <feather-icon
                        icon="ClockIcon"
                        size="14"
                        class="mr-1"
                      />
                      Submitted: {{ formatDateTime(tripRating.riderRating.createdAt) }}
                    </small>
                  </div>
                </template>
                <template v-else>
                  <div class="text-center text-muted py-3">
                    <feather-icon
                      icon="StarIcon"
                      size="48"
                      class="text-light mb-2"
                    />
                    <p>
                      No rider rating available
                    </p>
                  </div>
                </template>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </template>
      <template v-else-if="!loading && !tripRating">
        <div class="text-center text-muted py-4">
          <feather-icon
            icon="StarIcon"
            size="48"
            class="text-light mb-3"
          />
          <h5>No Ratings Available</h5>
          <p>No ratings have been submitted for this trip yet.</p>
        </div>
      </template>
    </base-overlay>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol,
} from 'bootstrap-vue'
import { inject, ref, onMounted } from '@vue/composition-api'
import axiosIns from '@/libs/axios'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
  },
  setup() {
    const tripDetails = inject('trip')
    const loading = ref(false)
    const tripRating = ref(null)

    const fetchTripRating = async () => {
      if (!tripDetails.value?.id) return

      try {
        loading.value = true
        const res = await axiosIns.get(`/trips/${tripDetails.value.id}/rating`)
        tripRating.value = res.data.data
      } catch (error) {
        console.log('Error fetching trip rating:', error)
        tripRating.value = null
      } finally {
        loading.value = false
      }
    }

    const formatDateTime = dateString => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    }

    onMounted(() => {
      fetchTripRating()
    })

    return {
      loading,
      tripRating,
      formatDateTime,
    }
  },
}
</script>

<style scoped>
.rating-stars {
  display: flex;
  gap: 2px;
}

.text-warning {
  color: #ffc107 !important;
}

.text-light {
  color: #f8f9fa !important;
}

.b-card-header h5 {
  color: #5e72e4;
}

.b-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.b-card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}
</style>
